import { useState } from 'react';
import { useChargePoints, useTransactions } from '../lib/queries';
import { Chip, Empty, Loading, Pill, Tile } from '../components/ui';
import { loadFailedHint } from '../lib/errors';

const num = (v: unknown): number => {
  const n = typeof v === 'string' ? parseFloat(v) : typeof v === 'number' ? v : 0;
  return Number.isFinite(n) ? n : 0;
};
const kwh = (v: unknown) => num(v).toFixed(2);

/* -------------------------------------------------------- transactions --- */

export function Transactions() {
  const { data, isLoading, error } = useTransactions();
  const { data: st } = useChargePoints();
  const [only, setOnly] = useState<'all' | 'active' | 'done'>('all');
  if (isLoading) return <Loading />;
  // A failed load is said, not spun on forever.
  if (!data) return <Empty title="Transactions are not available." hint={loadFailedHint(error)} />;

  const nameOf = (id: number) =>
    st?.items.find((s) => s.csmsId === id)?.name ?? `Charge point ${id}`;

  const rows = data.items.filter((t) =>
    only === 'all' ? true : only === 'active' ? t.isActive : !t.isActive);
  const energy = data.items.reduce((n, t) => n + num(t.totalKwh), 0);
  /* Only what was actually charged to a wallet.
     This used to sum `totalCost`, which is CitrineOS's own column and which
     nothing ever writes — so every figure here was 0.00 while real money was
     moving. The server now reports what our ledger holds, and marks anything
     it could only compute from a tariff. */
  const revenue = data.items.reduce(
    (n, t) => n + (t.billed ? num(t.cost) : 0), 0);
  const estimated = data.items.filter((t) => !t.billed && t.cost != null).length;
  const active = data.items.filter((t) => t.isActive).length;

  return (
    <>
      <div className="tiles">
        <Tile k="Sessions" v={data.items.length} d="on record in the CSMS" />
        <Tile k="Running now" v={active} d={active ? 'in progress' : 'none active'} />
        <Tile k="Energy" v={energy.toFixed(1)} d="kWh total" />
        <Tile
          k="Revenue"
          v={revenue.toFixed(2)}
          d={estimated
            ? `billed · ${estimated} older not priced`
            : 'billed to wallets'}
        />
      </div>

      <div className="toolbar">
        <div className="chips">
          {(['all', 'active', 'done'] as const).map((f) => (
            <Chip key={f} selected={only === f} onClick={() => setOnly(f)}
                  label={f === 'all' ? 'All' : f === 'active' ? 'Running' : 'Finished'} />
          ))}
        </div>
        <Pill tone="on">live from the CSMS</Pill>
      </div>

      {!rows.length ? (
        <Empty
          title="No sessions on record."
          hint="Transactions appear here once a charger reports a StartTransaction."
        />
      ) : (
        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th>Transaction</th><th>Charge point</th><th className="num">Connector</th>
                <th className="num">kWh</th><th className="num">Cost</th>
                <th>Started</th><th>State</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.transactionId}>
                  <td className="mono">{t.transactionId}</td>
                  <td>{nameOf(t.stationId)}</td>
                  <td className="num mono">{t.connectorId ?? '—'}</td>
                  <td className="num mono">{kwh(t.totalKwh)}</td>
                  {/* A charge that was billed reads plainly; one we could only
                      work out from the tariff is marked, because quoting an
                      estimate as though somebody paid it is how a dispute
                      starts. A dash means there is no tariff to compute from. */}
                  <td
                    className="num mono"
                    title={t.billed
                      ? 'Charged to the driver’s wallet'
                      : 'Not billed — computed from the charge point tariff'}
                  >
                    {t.cost == null
                      ? '—'
                      : t.billed
                        ? num(t.cost).toFixed(2)
                        : `~${num(t.cost).toFixed(2)}`}
                  </td>
                  <td className="mono">{t.startTime ? new Date(t.startTime).toLocaleString() : '—'}</td>
                  <td>
                    <Pill tone={t.isActive ? 'mid' : 'on'}>
                      {t.isActive ? (t.chargingState ?? 'Charging') : (t.stoppedReason ?? 'Completed')}
                    </Pill>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
